import type { ReactNode } from "react";
import type { EChartsOption } from "echarts";
import { Spinner } from "@fluentui/react-components";
import { EChartsWrapper } from "../EChartsWrapper/EChartsWrapper";
import { MetricInfo } from "../MetricInfo/MetricInfo";
import type { IMetricHelp } from "../MetricInfo/MetricInfo.types";
import { useStyles } from "./SectionChart.styles";

interface ILegendItem {
  name: string;
  color: string;
  dashed?: boolean;
}

interface ISectionChartProps {
  title: string;
  /** Secondary line under the title, for caveats the title has no room for. */
  subtitle?: string;
  /** Controls rendered in the header, left of the legend (e.g. a unit toggle). */
  actions?: ReactNode;
  option?: EChartsOption;
  height?: string;
  legend?: ILegendItem[];
  loading?: boolean;
  emptyMessage?: string;
  info?: IMetricHelp;
  unavailable?: boolean;
  unavailableMessage?: string;
}

const DEFAULT_UNAVAILABLE_MESSAGE = "Not available for this selection";

export const SectionChart = ({
  title,
  subtitle,
  actions,
  option,
  height,
  legend,
  loading,
  emptyMessage,
  info,
  unavailable,
  unavailableMessage,
}: ISectionChartProps): JSX.Element => {
  const styles = useStyles();
  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <div className={styles.title}>
            {title}
            {info && <MetricInfo info={info} label={title} />}
          </div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
        {(actions || legend) && (
          <div className={styles.legend}>
            {actions}
            {legend?.map(({ name, color, dashed }) => (
              <span key={name} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={
                    dashed
                      ? {
                          background: "transparent",
                          border: `2px dashed ${color}`,
                        }
                      : { background: color }
                  }
                />
                <span className={styles.legendLabel}>{name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      {unavailable ? (
        <div className={styles.unavailableContainer}>
          {unavailableMessage ?? DEFAULT_UNAVAILABLE_MESSAGE}
        </div>
      ) : loading ? (
        <div className={styles.loadingContainer}>
          <Spinner size="medium" />
        </div>
      ) : emptyMessage || !option ? (
        <div className={styles.emptyContainer}>
          {emptyMessage ?? "No data available"}
        </div>
      ) : (
        <EChartsWrapper option={option} height={height} />
      )}
    </div>
  );
};
